import { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
  Button,
} from '@mui/material';
import { CSS } from '@dnd-kit/utilities';
import { SxProps, Theme, useTheme } from '@mui/system';
import {
  DTA_IMAGE_INSPECTION,
  INITIAL_X_POSITION,
  INITIAL_Y_POSITION,
  NUMBER_OF_MAKER,
  RADIUS_OF_MAKER,
} from '../../../../../../_constants/inspectionConst';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../../../stores/store';
import { updateMarker } from '../../../../../../stores/inspectionTypeData/slice';
import { MarkerInfo } from '../../../../../../services/apiTypes';
import { useTranslation } from 'react-i18next';
import { DTA_IMAGE_SIZE } from '../../../../../../_constants/styleConst';
import ClearIcon from '@mui/icons-material/Clear';
import { getDtaImage } from '../../../../../../_utils/inspectionUtils';
import { MaxLength } from '../../../../../../_constants/maxLengthConst';

const Marker = (props: {
  id: number;
  left: number;
  top: number;
  ref: RefObject<HTMLDivElement | null>;
}) => {
  const theme = useTheme();
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: `marker-${props.id}`,
  });

  const combinedRef = (node: HTMLDivElement | null) => {
    setNodeRef(node);
    props.ref.current = node;
  };

  const style: SxProps<Theme> = {
    touchAction: 'none',
    position: 'absolute',
    top: props.top,
    left: props.left,
    width: RADIUS_OF_MAKER * 2,
    height: RADIUS_OF_MAKER * 2,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.background.default,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    cursor: 'move',
    transform: CSS.Translate.toString(transform),
    zIndex: NUMBER_OF_MAKER - props.id, // 1番が上に表示されるようにする
  };

  return (
    <Box ref={combinedRef} {...listeners} {...attributes} sx={{ ...style }}>
      {props.id}
    </Box>
  );
};

/** マーカーの初期値を取得 */
const getDefaultMarkers = () => {
  const markers: MarkerInfo[] = [];
  for (let i = 0; i < NUMBER_OF_MAKER; i++) {
    markers.push({
      id: i,
      x: INITIAL_X_POSITION,
      y: INITIAL_Y_POSITION,
      memo: '',
    });
  }
  return markers;
};

type DtaImageProps = {
  markers: MarkerInfo[];
};

const DtaImage: React.FC<DtaImageProps> = ({ markers }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { setNodeRef } = useDroppable({ id: 'image-area' });
  const theme = useTheme();
  const { typeId } = useSelector((state: RootState) => state.selectIds);
  const { repairRequest } = useSelector(
    (state: RootState) => state.repairRequest,
  );

  /** メモdispatch前の一時マーカー情報 */
  const [tempMarkers, setTempMarkers] = useState<MarkerInfo[]>(markers);

  /** 場所の画像を取得する */
  const imageRef = useRef<HTMLDivElement>(null);
  /** 場所のマーカーを取得する */
  const makerRef = useRef<HTMLDivElement>(null);

  /** 先端画像の切り替え */
  const bgImage = useMemo(() => {
    return getDtaImage(repairRequest);
  }, []);

  useEffect(() => {
    if (
      DTA_IMAGE_INSPECTION.typeIdList.includes(typeId) &&
      markers.length < NUMBER_OF_MAKER
    ) {
      const defaultMarkers = getDefaultMarkers();
      defaultMarkers.forEach((marker, i) => {
        if (!markers[i]) {
          dispatch(updateMarker({ marker }));
        }
      });
    }
    setTempMarkers(markers);
  }, [markers]);

  /** 使用中のマーカー数を返す　*/
  const numberOfUsingMarkers = () => {
    if (markers.length === 0) {
      return 0;
    }
    return markers.filter((value) => {
      return value.x !== INITIAL_X_POSITION || value.y !== INITIAL_Y_POSITION;
    }).length;
  };

  const handleMarkPositionChange = (
    markerId: number,
    position: { x: number; y: number },
  ) => {
    if (markers.length > 0 && markers[markerId]) {
      if (checkIsTouching()) {
        dispatch(
          updateMarker({
            marker: {
              ...markers[markerId],
              x: position.x,
              y: position.y,
            },
          }),
        );
        return;
      }
      handleDelete(markerId);
    }
  };

  const handleMemoChange = (markerId: number, value: string) => {
    if (tempMarkers.length > 0 && tempMarkers[markerId]) {
      setTempMarkers((prev) => {
        return [...prev].map((marker, i) => {
          if (markerId === i) {
            return { ...marker, memo: value };
          }
          return { ...marker };
        });
      });
    }
  };

  // 過剰レンダリング防止のため、フォーカスアウトしてからdispatch
  const handleMemoBlur = (markerId: number) => {
    dispatch(
      updateMarker({
        marker: {
          ...tempMarkers[markerId],
        },
      }),
    );
  };

  const combinedRef = (node: HTMLDivElement | null) => {
    setNodeRef(node);
    imageRef.current = node;
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (markers.length === 0) {
      return;
    }
    const { active, delta } = event;
    const markerId = parseInt((active.id as string).replace('marker-', ''), 10);
    const target = markers.find((marker) => marker.id === markerId - 1);
    if (!target) {
      return;
    }
    handleMarkPositionChange(target.id, {
      // APIリクエストにおける整合性のため整数にしておく
      x: Math.ceil(target.x + delta.x),
      y: Math.ceil(target.y + delta.y),
    });
  };

  /** マーカーが画像に接触しているか */
  const checkIsTouching = () => {
    const image = imageRef.current;
    const maker = makerRef.current;

    if (!image || !maker) return false;

    const imageRect = image.getBoundingClientRect();
    const makerRect = maker.getBoundingClientRect();

    const isTouching =
      imageRect.right >= makerRect.left &&
      imageRect.left <= makerRect.right &&
      imageRect.bottom >= makerRect.top &&
      imageRect.top <= makerRect.bottom;

    return isTouching;
  };

  /** マーカーを除外 */
  const handleDelete = (markerId: number) => {
    if (markers.length === 0) {
      return;
    }
    // 除外したマーカー以降を詰める
    for (let i = markerId; i < NUMBER_OF_MAKER - 1; i++) {
      dispatch(
        updateMarker({
          marker: {
            id: i,
            x: markers[i + 1].x,
            y: markers[i + 1].y,
            memo: markers[i + 1].memo,
          },
        }),
      );
    }
    // 最後のマーカーは初期化
    dispatch(
      updateMarker({
        marker: {
          id: NUMBER_OF_MAKER - 1,
          x: INITIAL_X_POSITION,
          y: INITIAL_Y_POSITION,
          memo: '',
        },
      }),
    );
  };

  return (
    <Paper
      sx={{
        mt: 10,
        mr: 1,
        orderRadius: '4px',
        bgcolor: 'rgba(0, 0, 0, 0.05)',
      }}
    >
      <Grid container spacing={1} display="flex" justifyContent="space-between">
        <Grid
          container
          size={1}
          mt={3}
          marginLeft={'80px'}
          width={'calc(100%)'}
        >
          <Typography>{t('MSG_DTA_MARKER_DESCRIPTION')}</Typography>
        </Grid>
        {/* マーカーエリア */}
        <Grid container size={6} mt={2} maxWidth={'300px'}>
          <Paper
            sx={{
              marginLeft: '10px',
              marginBottom: '10px',
              bgcolor: theme.palette.background.default,
            }}
          >
            <DndContext onDragEnd={handleDragEnd}>
              <Box
                ref={combinedRef}
                sx={{
                  position: 'relative',
                  margin: '10px',
                  width: DTA_IMAGE_SIZE,
                  height: DTA_IMAGE_SIZE,
                  backgroundImage: `url(images/` + bgImage + `.png)`,
                  backgroundSize: 'contain',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                }}
              >
                {markers.map((marker: MarkerInfo, i) => (
                  <Marker
                    key={`marker_${i}`}
                    ref={makerRef}
                    id={marker.id + 1}
                    left={marker.x}
                    top={marker.y}
                  />
                ))}
              </Box>
            </DndContext>
          </Paper>
        </Grid>

        {/* メモエリア */}
        <Grid container size={6} mt={2} width={'calc(100% - 310px)'}>
          <Typography>{t('LBL_MEMO')}</Typography>
          <TableContainer
            component={Paper}
            sx={{
              marginRight: '10px',
              marginBottom: '10px',
              bgcolor: theme.palette.background.default,
            }}
          >
            <Table>
              <TableBody>
                {Array.from({ length: NUMBER_OF_MAKER }, (_, i) => (
                  <TableRow
                    key={`marker_memo_${i}`}
                    sx={{ bgcolor: theme.palette.background.default }}
                  >
                    <TableCell align="center" sx={{ padding: 1 }}>
                      {i + 1}
                    </TableCell>
                    <TableCell sx={{ p: 1, pr: 0 }}>
                      <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{ bgcolor: theme.palette.background.default }}
                        slotProps={{
                          htmlInput: {
                            maxLength: MaxLength.DtaImage.MEMO,
                          },
                        }}
                        value={
                          tempMarkers.find((marker) => marker.id === i)?.memo ??
                          ''
                        }
                        onChange={(e) => handleMemoChange(i, e.target.value)}
                        onBlur={() => handleMemoBlur(i)}
                        disabled={i >= numberOfUsingMarkers()}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 0, px: 1 }}>
                      <Button
                        size="small"
                        sx={{ minWidth: '18px', p: '0' }}
                        onClick={() => handleDelete(i)}
                      >
                        {i < numberOfUsingMarkers() && (
                          <ClearIcon
                            sx={{
                              fontSize: '18px',
                              color: theme.palette.secondary.main,
                            }}
                          />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default DtaImage;
